import { useCallback, useEffect, useRef, useState } from "react";
import api, { getAllRecords } from "../api";
import SearchableSelect from "../components/SearchableSelect";
import TablePagination, { useTablePagination } from "../components/TablePagination";

const istDate = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};
const currentTime = () =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
const formatTime = (value) => {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ""))) return "—";
  const [hour, minute] = value.split(":").map(Number);
  return new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(2000, 0, 1, hour, minute));
};
function MeridiemTimeInput({ value, onChange, disabled, autoFocus = false }) {
  return <input className="meridiem-time-input" type="time" value={value || ""} onChange={(event) => onChange(event.target.value)} disabled={disabled} autoFocus={autoFocus} step="60" />;
}
const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(`${value}T00:00:00+05:30`))
    : "—";
const formatDuration = (minutes) =>
  `${Math.floor(Number(minutes || 0) / 60)}h ${Number(minutes || 0) % 60}m`;
const checkoutAt = (record) => {
  if (record.checkedOutAt) return new Date(record.checkedOutAt);
  if (!record.attendanceDate || !record.checkInTime || !record.checkOutTime)
    return null;
  const value = new Date(
    `${record.attendanceDate}T${record.checkOutTime}:00+05:30`,
  );
  if (record.checkOutTime < record.checkInTime)
    value.setDate(value.getDate() + 1);
  return value;
};
const canEditAfterCheckout = (record) => {
  if (record.status !== "completed" || !record.checkOutTime) return false;
  const completedAt = checkoutAt(record)?.getTime();
  const elapsed = Date.now() - completedAt;
  return Number.isFinite(completedAt) && elapsed >= 0 && elapsed <= 24 * 60 * 60 * 1000;
};
const canEditAttendance = (record) =>
  record.status === "active" || canEditAfterCheckout(record);
export default function StaffAttendancePage() {
  const [records, setRecords] = useState([]),
    [drivers, setDrivers] = useState([]),
    [vehicles, setVehicles] = useState([]),
    [clients, setClients] = useState([]),
    [vehicle, setVehicle] = useState(""),
    [driver, setDriver] = useState(""),
    [attendanceDate, setAttendanceDate] = useState(istDate()),
    [checkInTime, setCheckInTime] = useState(currentTime()),
    [meterReading, setMeterReading] = useState(""),
    [meterPopup, setMeterPopup] = useState(false),
    [checkoutRecord, setCheckoutRecord] = useState(null),
    [editingRecord, setEditingRecord] = useState(null),
    [editForm, setEditForm] = useState(null),
    [editLocations, setEditLocations] = useState([]),
    [checkoutTime, setCheckoutTime] = useState(currentTime()),
    [checkoutMeterReading, setCheckoutMeterReading] = useState(""),
    [query, setQuery] = useState(""),
    [notice, setNotice] = useState(""),
    [loading, setLoading] = useState(true),
    [savingCheckIn, setSavingCheckIn] = useState(false),
    [savingEdit, setSavingEdit] = useState(false),
    [checkingOutId, setCheckingOutId] = useState("");
  const creatingAttendance = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [attendanceRows, driverRows, vehicleRows, clientResult] = await Promise.all([
        getAllRecords("/attendance", { attendanceDate }),
        getAllRecords("/drivers"),
        getAllRecords("/vehicles"),
        api.get("/clients"),
      ]);
      setRecords(attendanceRows);
      setDrivers(driverRows);
      setVehicles(vehicleRows);
      setClients(clientResult.data?.data || []);
    } catch (error) {
      setNotice(error.response?.data?.message || "Unable to load attendance.");
    } finally {
      setLoading(false);
    }
  }, [attendanceDate]);
  useEffect(() => {
    load();
  }, [load]);
  const openMeterPopup = () => {
    if (!vehicle) return setNotice("Please select a vehicle.");
    if (!driver) return setNotice("Please select a driver.");
    if (!checkInTime) return setNotice("Please select a check-in time.");
    setMeterReading("");
    setMeterPopup(true);
  };
  const markPresent = async (event) => {
    event.preventDefault();
    if (creatingAttendance.current) return;
    if (
      meterReading === "" ||
      !Number.isFinite(Number(meterReading)) ||
      Number(meterReading) < 0
    )
      return setNotice("Please enter a valid vehicle meter reading.");
    creatingAttendance.current = true;
    setSavingCheckIn(true);
    try {
      await api.post("/attendance", {
        vehicle,
        driver,
        attendanceDate,
        checkInTime,
        checkInMeterReading: Number(meterReading),
      });
      setMeterPopup(false);
      setNotice("Attendance marked present and checked in.");
      await load();
    } catch (error) {
      setNotice(error.response?.data?.message || "Unable to mark attendance.");
    } finally {
      creatingAttendance.current = false;
      setSavingCheckIn(false);
    }
  };
  const openCheckoutPopup = (record) => {
    setCheckoutRecord(record);
    setCheckoutTime(currentTime());
    setCheckoutMeterReading("");
  };
  const openEditPopup = (record) => {
    const assignedVehicle = record.vehicle?._id
      ? record.vehicle
      : vehicles.find((item) => item.vehicleNumber === record.vehicleNumber);
    setEditingRecord(record);
    setEditForm({
      attendanceDate: record.attendanceDate || istDate(),
      vehicle: assignedVehicle?._id || "",
      driver: record.driver?._id || drivers.find((item) => item.fullName === record.driverName)?._id || "",
      checkInTime: record.checkInTime || "",
      checkInMeterReading: record.checkInMeterReading ?? "",
      checkOutTime: record.checkOutTime || "",
      checkOutMeterReading: record.checkOutMeterReading ?? "",
      client: assignedVehicle?.client?._id || assignedVehicle?.client || "",
      clientLocation:
        assignedVehicle?.clientLocation?._id || assignedVehicle?.clientLocation || "",
    });
  };
  useEffect(() => {
    if (!editForm?.client) {
      setEditLocations([]);
      return;
    }
    api
      .get(`/locations/client/${editForm.client}`, { params: { active: true } })
      .then((response) => setEditLocations(response.data?.data || []))
      .catch(() => setEditLocations([]));
  }, [editForm?.client]);
  const updateAttendance = async (event) => {
    event.preventDefault();
    if (!editingRecord || !editForm) return;
    setSavingEdit(true);
    try {
      const data = {
        attendanceDate: editForm.attendanceDate,
        vehicle: editForm.vehicle,
        driver: editForm.driver,
        checkInTime: editForm.checkInTime,
        checkInMeterReading: Number(editForm.checkInMeterReading),
        client: editForm.client,
        clientLocation: editForm.clientLocation,
      };
      if (editingRecord.status === "completed") {
        data.checkOutTime = editForm.checkOutTime;
        data.checkOutMeterReading = Number(editForm.checkOutMeterReading);
      }
      await api.put(`/attendance/${editingRecord._id}`, data);
      setEditingRecord(null);
      setEditForm(null);
      setNotice("Attendance updated.");
      await load();
    } catch (error) {
      setNotice(error.response?.data?.message || "Unable to update attendance.");
    } finally {
      setSavingEdit(false);
    }
  };
  const checkOut = async (event) => {
    event.preventDefault();
    if (!checkoutRecord || !/^([01]\d|2[0-3]):[0-5]\d$/.test(checkoutTime))
      return setNotice("Please select a valid check-out time.");
    const reading = Number(checkoutMeterReading);
    if (
      checkoutMeterReading === "" ||
      !Number.isFinite(reading) ||
      reading < Number(checkoutRecord.checkInMeterReading || 0)
    )
      return setNotice(
        `Enter a check-out kilometre reading of at least ${checkoutRecord.checkInMeterReading || 0}.`,
      );
    setCheckingOutId(checkoutRecord._id);
    try {
      await api.post(`/attendance/${checkoutRecord._id}/checkout`, {
        checkOutTime: checkoutTime,
        checkOutMeterReading: reading,
      });
      setCheckoutRecord(null);
      setNotice("Check-out and kilometre reading recorded.");
      await load();
    } catch (error) {
      setNotice(error.response?.data?.message || "Unable to check out.");
    } finally {
      setCheckingOutId("");
    }
  };
  const filtered = records.filter((record) =>
    `${record.driver?.fullName || record.driverName || ""} ${record.vehicle?.vehicleNumber || record.vehicleNumber || ""} ${record.status || ""}`
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  const { page, setPage, pageRows } = useTablePagination(filtered);
  return (
    <div className="fuel-mis attendance-page">
      <div className="fuel-mis-heading">
        <div>
          <p>ATTENDANCE</p>
          <h2>Driver Attendance</h2>
          <span>Record each driver shift or trip separately. Multiple attendance entries can use the same driver and check-in time.</span>
        </div>
      </div>
      {notice && (
        <div className="fuel-notice">
          {notice}
          <button onClick={() => setNotice("")}>×</button>
        </div>
      )}
      <section className="vehicle-presence-panel">
        <div>
          <p>VEHICLE & DRIVER ATTENDANCE</p>
          <h3>Attendance selection</h3>
          <span>Mark each shift or trip with its own check-in time and kilometre reading.</span>
        </div>
        <div className="vehicle-presence-controls">
          <label className="attendance-vehicle-field">
            Vehicle
            <SearchableSelect
              value={vehicle}
              options={vehicles.map((item) => ({
                value: item._id,
                label: item.vehicleNumber,
              }))}
              placeholder="Search vehicle"
              vehicleNumberSearch
              onChange={setVehicle}
              disabled={savingCheckIn}
            />
          </label>
          <label className="attendance-driver-field">
            Driver
            <SearchableSelect
              value={driver}
              options={drivers.map((item) => ({
                value: item._id,
                label: item.isVendorDriver
                  ? `${item.fullName} (Temporary)`
                  : item.fullName,
              }))}
              placeholder="Search driver"
              onChange={setDriver}
              disabled={savingCheckIn}
            />
          </label>
          <label>
            Date
            <input
              type="date"
              value={attendanceDate}
              onChange={(event) => setAttendanceDate(event.target.value)}
              disabled={savingCheckIn}
            />
          </label>
          <div className="timing-actions">
            <label>
              Check-in time
              <MeridiemTimeInput value={checkInTime} onChange={setCheckInTime} disabled={savingCheckIn} />
            </label>
            <button disabled={savingCheckIn} onClick={openMeterPopup}>
              Mark present
            </button>
          </div>
        </div>
      </section>
      <section className="attendance-records">
        <div className="fuel-tools">
          <input
            placeholder="Search driver, vehicle, or status"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setPage(1); }}
          />
          <button disabled={loading} onClick={load}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {loading ? (
          <p>Loading attendance…</p>
        ) : (
          <div className="fuel-table">
            <table>
              <thead>
                <tr>
                  {[
                    "Date",
                    "Vehicle",
                    "Client Name",
                    "Hub-Location",
                    "Fixed Vehicle / Adhoc Vehicle",
                    "Driver",
                    "Check in",
                    "Check out",
                    "Duration",
                    "Status",
                    "Overtime",
                    "Actions",
                  ].map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((record) => (
                  <tr key={record._id}>
                    <td>{formatDate(record.attendanceDate)}</td>
                    <td>
                      {record.vehicle?.vehicleNumber || record.vehicleNumber}
                    </td>
                    <td>{record.vehicle?.clientName || "—"}</td>
                    <td>{record.vehicle?.clientLocationName || "—"}</td>
                    <td>
                      {record.driver?.isVendorDriver
                        ? "Adhoc Vehicle"
                        : "Fixed Vehicle"}
                    </td>
                    <td>{record.driver?.fullName || record.driverName}</td>
                    <td>{formatTime(record.checkInTime)}</td>
                    <td>{formatTime(record.checkOutTime)}</td>
                    <td>{formatDuration(record.durationMinutes)}</td>
                    <td>
                      <span className={`attendance-state ${record.status}`}>
                        {record.status === "completed" ? "Completed" : "Active"}
                      </span>
                    </td>
                    <td>{formatDuration(record.overtimeMinutes)}</td>
                    <td>
                      {record.status === "active" && (
                        <button
                          className="attendance-checkout-button"
                          disabled={checkingOutId === record._id}
                          onClick={() => openCheckoutPopup(record)}
                        >
                          {checkingOutId === record._id
                            ? "Checking out…"
                            : "Check out"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openEditPopup(record)}
                        disabled={!canEditAttendance(record)}
                        title={
                          canEditAttendance(record)
                            ? "Edit attendance, client name, and hub-location."
                            : "The 24-hour edit window has expired."
                        }
                      >
                        {canEditAttendance(record) ? "Edit" : "Edit expired"}
                      </button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan="12">
                      No attendance records found for this date.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <TablePagination page={page} setPage={setPage} total={filtered.length} label="Attendance records" />
      </section>
      {meterPopup && (
        <div
          className="fuel-modal-backdrop"
        >
          <form
            className="fuel-modal"
            onSubmit={markPresent}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p>VEHICLE METER</p>
                <h3>Enter current kilometre reading</h3>
              </div>
              <button
                type="button"
                onClick={() => setMeterPopup(false)}
                disabled={savingCheckIn}
              >
                ×
              </button>
            </header>
            <div className="fuel-form-grid">
              <label className="wide">
                Current meter reading (KM)
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={meterReading}
                  onChange={(event) => setMeterReading(event.target.value)}
                  placeholder="Enter current kilometre reading"
                  autoFocus
                  required
                  disabled={savingCheckIn}
                />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="cancel"
                onClick={() => setMeterPopup(false)}
                disabled={savingCheckIn}
              >
                Cancel
              </button>
              <button disabled={savingCheckIn}>
                {savingCheckIn ? "Marking…" : "Save & Mark present"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {checkoutRecord && (
        <div
          className="fuel-modal-backdrop"
        >
          <form
            className="fuel-modal"
            onSubmit={checkOut}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p>ATTENDANCE CHECK-OUT</p>
                <h3>Confirm actual check-out details</h3>
              </div>
              <button
                type="button"
                onClick={() => setCheckoutRecord(null)}
                disabled={Boolean(checkingOutId)}
              >
                ×
              </button>
            </header>
            <div className="fuel-form-grid">
              <label>
                Actual check-out time
                <MeridiemTimeInput value={checkoutTime} onChange={setCheckoutTime} autoFocus disabled={Boolean(checkingOutId)} />
              </label>
              <label>
                Check-out metre reading (KM)
                <input
                  type="number"
                  min={checkoutRecord.checkInMeterReading || 0}
                  step="0.1"
                  value={checkoutMeterReading}
                  onChange={(event) =>
                    setCheckoutMeterReading(event.target.value)
                  }
                  placeholder={`Minimum: ${checkoutRecord.checkInMeterReading || 0}`}
                  required
                  disabled={Boolean(checkingOutId)}
                />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="cancel"
                onClick={() => setCheckoutRecord(null)}
                disabled={Boolean(checkingOutId)}
              >
                Cancel
              </button>
              <button disabled={Boolean(checkingOutId)}>
                {checkingOutId ? "Saving…" : "Save check-out"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {editingRecord && editForm && (
        <div
          className="fuel-modal-backdrop"
        >
          <form
            className="fuel-modal"
            onSubmit={updateAttendance}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p>ATTENDANCE</p>
                <h3>Edit attendance entry</h3>
              </div>
              <button
                type="button"
                onClick={() => setEditingRecord(null)}
                disabled={savingEdit}
              >
                ×
              </button>
            </header>
            <div className="fuel-form-grid">
              <label>
                Vehicle
                <SearchableSelect
                  value={editForm.vehicle}
                  options={vehicles.map((item) => ({
                    value: item._id,
                    label: item.vehicleNumber,
                  }))}
                  placeholder="Search vehicle number"
                  vehicleNumberSearch
                  onChange={(vehicleId) => {
                    const selectedVehicle = vehicles.find(
                      (item) => item._id === vehicleId,
                    );
                    setEditForm((current) => ({
                      ...current,
                      vehicle: vehicleId,
                      // Vehicle selection must not overwrite the driver. Staff
                      // can choose or change the driver independently below.
                      driver: current.driver,
                      client:
                        selectedVehicle?.client?._id ||
                        selectedVehicle?.client ||
                        "",
                      clientLocation:
                        selectedVehicle?.clientLocation?._id ||
                        selectedVehicle?.clientLocation ||
                        "",
                    }));
                  }}
                  disabled={savingEdit}
                  required
                />
              </label>
              <label>
                Driver
                <SearchableSelect
                  value={editForm.driver}
                  options={drivers.map((item) => ({
                    value: item._id,
                    label: item.isVendorDriver
                      ? `${item.fullName} (Temporary)`
                      : item.fullName,
                  }))}
                  placeholder="Search driver name"
                  onChange={(driverId) => {
                    setEditForm((current) => ({
                      ...current,
                      driver: driverId,
                    }));
                  }}
                  disabled={savingEdit}
                  required
                />
              </label>
              <label>
                Client Name
                <select
                  value={editForm.client}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      client: event.target.value,
                      clientLocation: "",
                    }))
                  }
                  required
                  disabled={savingEdit}
                >
                  <option value="">Select client</option>
                  {clients
                    .filter((client) => client.status === "active")
                    .map((client) => (
                      <option key={client._id} value={client._id}>
                        {client.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Hub-Location
                <select
                  value={editForm.clientLocation}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      clientLocation: event.target.value,
                    }))
                  }
                  required
                  disabled={savingEdit || !editForm.client}
                >
                  <option value="">Select Hub-Location</option>
                  {editLocations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="attendance-date-field">
                Attendance date
                <input
                  type="date"
                  value={editForm.attendanceDate}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      attendanceDate: event.target.value,
                    }))
                  }
                  required
                  disabled={savingEdit}
                />
              </label>
              <label>
                Check-in time
                <MeridiemTimeInput value={editForm.checkInTime} onChange={(value) => setEditForm((current) => ({ ...current, checkInTime: value }))} disabled={savingEdit} />
              </label>
              <label>
                Check-in metre reading (KM)
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={editForm.checkInMeterReading}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      checkInMeterReading: event.target.value,
                    }))
                  }
                  required
                  disabled={savingEdit}
                />
              </label>
              {editingRecord.status === "completed" && (
                <>
                  <label>
                    Check-out time
                    <MeridiemTimeInput value={editForm.checkOutTime} onChange={(value) => setEditForm((current) => ({ ...current, checkOutTime: value }))} disabled={savingEdit} />
                  </label>
                  <label>
                    Check-out metre reading (KM)
                    <input
                      type="number"
                      min={editForm.checkInMeterReading || 0}
                      step="0.1"
                      value={editForm.checkOutMeterReading}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          checkOutMeterReading: event.target.value,
                        }))
                      }
                      required
                      disabled={savingEdit}
                    />
                  </label>
                </>
              )}
            </div>
            <footer>
              <button
                type="button"
                className="cancel"
                onClick={() => setEditingRecord(null)}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button disabled={savingEdit}>
                {savingEdit ? "Saving…" : "Update attendance"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
